import React from 'react'

const CHUNK_ERROR_PATTERNS = [
  'Failed to fetch dynamically imported module',
  'Loading chunk',
  'Loading CSS chunk',
  'ChunkLoadError',
  'Unexpected token',
  'MIME type',
  'Expected a JavaScript-or-Wasm module script'
]

const RECOVERY_STORAGE_KEY = 'chunk_recovery_state'
const VERSION_CHECK_URL = '/version.json'
const VERSION_CHECK_INTERVAL_MS = 60000
const MAX_AUTO_RELOADS = 1
const AUTO_RELOAD_WINDOW_MS = 30000
const STABILITY_EVENT = 'app:stability-issue'

type StabilityIssueType = 'chunk-error' | 'version-mismatch'

interface RecoveryState {
  attempts: number
  lastAttemptAt: number
  lastBuildId: string | null
}

interface StabilityIssueDetail {
  type: StabilityIssueType
  message: string
  currentBuildId: string | null
  latestBuildId?: string | null
}

let listenersInstalled = false
let versionPollStarted = false

const defaultRecoveryState: RecoveryState = {
  attempts: 0,
  lastAttemptAt: 0,
  lastBuildId: null
}

const getBuildId = (): string | null => {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="app-build-id"]')
  return meta?.content || (typeof __APP_BUILD_ID__ !== 'undefined' ? __APP_BUILD_ID__ : null)
}

const readRecoveryState = (): RecoveryState => {
  try {
    const raw = sessionStorage.getItem(RECOVERY_STORAGE_KEY)
    if (!raw) {
      return { ...defaultRecoveryState, lastBuildId: getBuildId() }
    }

    return {
      ...defaultRecoveryState,
      ...JSON.parse(raw)
    }
  } catch {
    return { ...defaultRecoveryState, lastBuildId: getBuildId() }
  }
}

const writeRecoveryState = (state: RecoveryState) => {
  sessionStorage.setItem(RECOVERY_STORAGE_KEY, JSON.stringify(state))
}

const resetRecoveryState = () => {
  writeRecoveryState({
    attempts: 0,
    lastAttemptAt: 0,
    lastBuildId: getBuildId()
  })
}

const dispatchStabilityIssue = (detail: StabilityIssueDetail) => {
  window.dispatchEvent(new CustomEvent<StabilityIssueDetail>(STABILITY_EVENT, { detail }))
}

const forceReload = () => {
  const url = new URL(window.location.href)
  url.searchParams.set('__refresh', Date.now().toString())
  window.location.replace(url.toString())
}

const clearServiceWorkerState = async () => {
  try {
    if ('caches' in window) {
      const cacheKeys = await caches.keys()
      await Promise.all(cacheKeys.map((key) => caches.delete(key)))
    }
  } catch (error) {
    console.warn('Cache cleanup skipped:', error)
  }

  try {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations()
      await Promise.all(registrations.map((registration) => registration.unregister()))
    }
  } catch (error) {
    console.warn('Service worker cleanup skipped:', error)
  }
}

export const isChunkLoadError = (error: unknown): boolean => {
  const message =
    (error instanceof Error ? error.message : String(error || '')) +
    ' ' +
    ((error as any)?.name || '') +
    ' ' +
    ((error as any)?.toString?.() || '')

  return CHUNK_ERROR_PATTERNS.some((pattern) => message.includes(pattern))
}

const fetchVersionInfo = async () => {
  const response = await fetch(`${VERSION_CHECK_URL}?t=${Date.now()}`, {
    cache: 'no-store',
    headers: {
      'Cache-Control': 'no-cache'
    }
  })

  if (!response.ok) {
    throw new Error(`Version check failed: ${response.status}`)
  }

  return response.json()
}

const handleVersionMismatch = async () => {
  if (!import.meta.env.PROD) {
    return false
  }

  try {
    const currentBuildId = getBuildId()
    const latest = await fetchVersionInfo()
    const latestBuildId = latest?.buildId || null

    if (currentBuildId && latestBuildId && currentBuildId !== latestBuildId) {
      await clearServiceWorkerState()
      dispatchStabilityIssue({
        type: 'version-mismatch',
        message: 'Uygulamanın yeni bir sürümü yayınlandı.',
        currentBuildId,
        latestBuildId
      })
      return true
    }
  } catch (error) {
    console.warn('Version check skipped:', error)
  }

  return false
}

export const handleRuntimeChunkError = async (error: unknown, source = 'unknown'): Promise<boolean> => {
  if (!isChunkLoadError(error)) {
    return false
  }

  const currentBuildId = getBuildId()
  const state = readRecoveryState()
  const now = Date.now()
  const sameWindow = now - state.lastAttemptAt < AUTO_RELOAD_WINDOW_MS
  const attempts = sameWindow ? state.attempts : 0

  console.error('Chunk/runtime load error detected:', {
    source,
    message: error instanceof Error ? error.message : String(error),
    currentBuildId,
    attempts
  })

  const hasVersionMismatch = await handleVersionMismatch()
  if (!hasVersionMismatch && attempts < MAX_AUTO_RELOADS) {
    writeRecoveryState({
      attempts: attempts + 1,
      lastAttemptAt: now,
      lastBuildId: currentBuildId
    })
    forceReload()
    return true
  }

  dispatchStabilityIssue({
    type: 'chunk-error',
    message:
      'Bazı uygulama dosyaları yüklenemedi. Bu genellikle deploy sonrası sürüm uyumsuzluğu veya bozuk asset yönlendirmesi anlamına gelir.',
    currentBuildId
  })
  return true
}

export const initializeRuntimeStability = () => {
  if (!listenersInstalled) {
    listenersInstalled = true

    window.addEventListener('error', (event) => {
      if (event.error && isChunkLoadError(event.error)) {
        void handleRuntimeChunkError(event.error, 'window.error')
        event.preventDefault()
      }
    })

    window.addEventListener('unhandledrejection', (event) => {
      if (event.reason && isChunkLoadError(event.reason)) {
        void handleRuntimeChunkError(event.reason, 'window.unhandledrejection')
        event.preventDefault()
      }
    })
  }

  if (!versionPollStarted && import.meta.env.PROD) {
    versionPollStarted = true
    resetRecoveryState()

    const runVersionCheck = () => {
      void handleVersionMismatch()
    }

    window.setInterval(runVersionCheck, VERSION_CHECK_INTERVAL_MS)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        runVersionCheck()
      }
    })
  }
}

export const lazyWithRetry = (importFn: () => Promise<any>) =>
  React.lazy(async () => {
    try {
      return await importFn()
    } catch (error) {
      await handleRuntimeChunkError(error, 'lazy-import')
      throw error
    }
  })

export { STABILITY_EVENT }
